import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const MapComponent = ({ pickups = [], orderItems = [], dropoff = [] }) => {
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const endMarkerRef = useRef(null);
  const polylineRef = useRef(null);

  // Initialize map only once
  useEffect(() => {
    if (!mapRef.current) {
      const firstPoint = pickups[0] || dropoff[0] || { lat: 0, long: 0 };
      mapRef.current = L.map("map", {
        center: [Number(firstPoint.lat), Number(firstPoint.long)],
        zoom: 13,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(
        mapRef.current
      );
    }

    return () => {
      // Clean up on unmount
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      markersRef.current = [];
      endMarkerRef.current = null;
      polylineRef.current = null;
    };
  }, []);

  // Update markers and polyline when data changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Clear old markers
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    // Add pickups
    pickups.forEach((p, idx) => {
      if (!p.lat || !p.long) return;
      const marker = L.marker([Number(p.lat), Number(p.long)], {
        icon: L.icon({
          iconUrl: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
          iconSize: [25, 25],
        }),
      })
        .addTo(map)
        .bindPopup(`Pickup ${idx + 1} - ${p.pickupaddress}`);
      markersRef.current.push(marker);
    });

    // Dropoff marker
    if (endMarkerRef.current) endMarkerRef.current.remove();
    if (dropoff[0]?.lat && dropoff[0]?.long) {
      endMarkerRef.current = L.marker(
        [Number(dropoff[0].lat), Number(dropoff[0].long)],
        {
          icon: L.icon({
            iconUrl: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
            iconSize: [25, 25],
          }),
        }
      )
        .addTo(map)
        .bindPopup(`Dropoff - ${dropoff[0].pickupaddress}`);
    }

    // Build full route
    const route = [
      ...pickups
        .map((p) => (p.lat && p.long ? [Number(p.lat), Number(p.long)] : null))
        .filter(Boolean),
      ...orderItems
        .map((o) => (o.lat && o.long ? [Number(o.lat), Number(o.long)] : null))
        .filter(Boolean),
      ...(dropoff[0]?.lat && dropoff[0]?.long
        ? [[Number(dropoff[0].lat), Number(dropoff[0].long)]]
        : []),
    ];

    if (polylineRef.current) {
      polylineRef.current.setLatLngs(route);
    } else if (route.length > 1) {
      polylineRef.current = L.polyline(route, { color: "purple" }).addTo(map);
    }

    // Fit bounds if multiple points
    if (route.length > 1) {
      map.fitBounds(L.latLngBounds(route));
    } else if (route.length === 1) {
      map.setView(route[0], 13);
    }
  }, [pickups, orderItems, dropoff]);

  return <div id="map" style={{ height: "100%", width: "100%" }} />;
};

export default MapComponent;
